import { Specification, Spine } from '@1x2network/pixi_assets'
import { JackpotRecord, JackpotWidgetGroup } from './jackpotwidgets/JackpotWidgetGroup'
import { Pot } from '@1x2network/false_persistence'
import { BasicLayout } from '@1x2network/layout'
import { SoundPlayer } from './SoundPlayer'
import { toSeconds } from '../utils/toSeconds'
import { staggerTimeline } from '../utils/gsap'
import gsap from 'gsap'
import { IPointData } from 'pixi.js'
import { gsapSpineWrapper } from '@1x2network/tweens'

/**
 * Handles animations of components when introducing the base game
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */

export class BaseIntroductionAnimation {
    private soundPlayer: SoundPlayer
    private specification: IntroductionAnimationSpecification
    private pot: Pot
    private layout: BasicLayout
    private jackpotWidgets: JackpotWidgetGroup
    private timeline?: gsap.core.Timeline

    constructor(options: {
        soundPlayer: SoundPlayer
        jackpotWidgets: JackpotWidgetGroup
        pot: Pot
        layout: BasicLayout
    }) {
        this.specification = Specification.get('introductionAnimation') as IntroductionAnimationSpecification
        this.pot = options.pot
        this.jackpotWidgets = options.jackpotWidgets
        this.layout = options.layout
        this.soundPlayer = options.soundPlayer
    }

    private introduceWidgets() {
        const { finalPositions, widgets } = this.jackpotWidgets

        const keys = Object.keys(widgets) as Array<keyof JackpotRecord<never>>
        const sortedkeys = keys.sort((a, b) => {
            const aWidget = widgets[a]
            const bWidget = widgets[b]

            return bWidget.y - aWidget.y
        })
        const animations: GSAPAnimation[] = []

        const { dropAngles, offset, tween } = this.specification.jackpotWidgets
        const { stagger, delay, dropDuration, rotateDuration } = tween
        sortedkeys.forEach((key) => {
            const widget = widgets[key]
            const targetY = finalPositions[key].y
            const startY = targetY + this.layout.top - widget.height * sortedkeys.length - offset
            widget.y = startY
            widget.angle = dropAngles[key]
            const tl = gsap
                .timeline()
                .to(widget, { y: targetY, ease: 'none', duration: toSeconds(dropDuration) }, toSeconds(delay))
                .call(() => this.soundPlayer.playIntroAnimationWidgetLand())
                .to(widget, { ease: 'bounce.out', angle: 0, duration: toSeconds(rotateDuration) })

            animations.push(tl)
        })

        return staggerTimeline(animations, toSeconds(stagger))
    }

    private introducePot() {
        const spine = this.pot.spine
        const timeline = gsap.timeline()
        const { initialPosition, endPosition: targetPosition, initialScale, waits, endScale } = this.specification.pot
        this.pot.position = initialPosition
        this.pot.scale.set(initialScale)
        spine.play('loop_04', 0, true)
        timeline.add(gsapSpineWrapper(spine, 0, 'increase_04-05'), toSeconds(waits.beforeReset))
        this.soundPlayer.playIntroAnimation()
        timeline
            .call(() => {
                spine.play('loop_05', 0, true)
            })
            .add(gsapSpineWrapper(spine, 0, 'decrease_05-00'), '>')
            .to(
                this.pot,
                { pixi: { ...targetPosition, scale: endScale }, duration: 0.5 },
                toSeconds(waits.beforeReturn),
            )

        return timeline
    }

    introduce() {
        const timeline = this.introduceWidgets()
        this.timeline = timeline
        this.timeline.add(this.introducePot(), '<')
        return timeline
    }
}

export interface IntroductionAnimationSpecification {
    jackpotWidgets: {
        offset: number
        dropAngles: JackpotRecord<number>
        tween: {
            stagger: number
            delay: number
            dropDuration: number
            rotateDuration: number
        }
    }
    pot: {
        initialPosition: IPointData
        endPosition: IPointData
        endScale: number
        initialScale: number
        waits: {
            beforeReset: number
            beforeReturn: number
        }
    }
}
